/**
 * Why a message sent while the agent works was refused or not delivered, as
 * the user reads it (mission stream contract phase 6, agion/Agion#2327): one
 * text per reason of the generated ReceiptReason table. The table's own
 * descriptions are for developers; these are for the person who sent it.
 *
 * RECEIPT_REASON_TEXTS is a record over ReceiptReason, so a reason added in
 * Go fails tsc here until it has a text.
 */

import type { ReceiptReason } from '@agion/mission-stream'

interface ReasonCopy {
  /** i18n key of the text */
  key: string
  /** English fallback when the key is missing */
  fallback: string
}

export const RECEIPT_REASON_TEXTS: Record<ReceiptReason, ReasonCopy> = {
  attachment_invalid: {
    key: 'chat:receiptReason.attachmentInvalid',
    fallback: "The attached files could not be verified. Remove and attach them again.",
  },
  attachment_host_mismatch: {
    key: 'chat:receiptReason.attachmentHostMismatch',
    fallback: "These files were uploaded to another server. Attach them to this run's server.",
  },
  attachment_host_ambiguous: {
    key: 'chat:receiptReason.attachmentHostAmbiguous',
    fallback: "The server that holds these files could not be verified. Attach the files again.",
  },
  attachment_too_many: {
    key: 'chat:receiptReason.attachmentTooMany',
    fallback: "This message has too many files. Remove some files and try again.",
  },
  attachment_unavailable: {
    key: 'chat:receiptReason.attachmentUnavailable',
    fallback: "The attached files are unavailable. Try again, or remove and attach them again.",
  },
  invalid_message: {
    key: 'chat:receiptReason.invalidMessage',
    fallback: 'The message could not be read.',
  },
  not_subscribed: {
    key: 'chat:receiptReason.notSubscribed',
    fallback: 'This chat is no longer following the run. Reload the page and try again.',
  },
  sender_not_allowed: {
    key: 'chat:receiptReason.senderNotAllowed',
    fallback: 'Only the person who started this run can send it messages.',
  },
  run_not_live: {
    key: 'chat:receiptReason.runNotLive',
    fallback: 'The run is not running any more.',
  },
  host_not_connected: {
    key: 'chat:receiptReason.hostNotConnected',
    fallback: 'The host that runs the assistant is not connected.',
  },
  runner_not_connected: {
    key: 'chat:receiptReason.runnerNotConnected',
    fallback: 'The host that runs the assistant is connected, but its runner is not.',
  },
  message_too_large: {
    key: 'chat:receiptReason.messageTooLarge',
    fallback: 'The message is too large to send.',
  },
  channel_full: {
    key: 'chat:receiptReason.channelFull',
    fallback: 'The connection to the assistant is busy. Try again in a moment.',
  },
  inbox_full: {
    key: 'chat:receiptReason.inboxFull',
    fallback: 'The assistant already has several messages waiting. Send this one after it reads them.',
  },
  mode_does_not_accept: {
    key: 'chat:receiptReason.modeDoesNotAccept',
    fallback: 'This run does not take messages while it works.',
  },
  duplicate: {
    key: 'chat:receiptReason.duplicate',
    fallback: 'The assistant already has this message.',
  },
  unknown_message_type: {
    key: 'chat:receiptReason.unknownMessageType',
    fallback: 'The server did not recognise the message; it may need an update.',
  },
  run_ended_before_delivery: {
    key: 'chat:receiptReason.runEndedBeforeDelivery',
    fallback: 'The run ended before the assistant read it.',
  },
}

type Translate = (key: string, fallback: string) => string

/** Whether this build has a text for the reason; a newer relay may send one it does not. */
export function hasReasonText(reason: string): reason is ReceiptReason {
  return Object.prototype.hasOwnProperty.call(RECEIPT_REASON_TEXTS, reason)
}

/** The translated text of a receipt reason. */
export function receiptReasonText(t: Translate, reason: ReceiptReason): string {
  const { key, fallback } = RECEIPT_REASON_TEXTS[reason]
  return t(key, fallback)
}
